import { compareAccDocKey } from "../../decorators/StoreIdEquality";
// import { stringifyKey } from "../../decorators/StoreIdHelper";
import { AcceleratorBatch } from "../../models/AcceleratorBatch";
import AcceleratorDoc, {
  AcceleratorDocStoreKey,
} from "../../models/AcceleratorDoc";
import { AcceleratorDocMetadata } from "../../models/AcceleratorDocMetadata";
import { Hydratable } from "../../models/hydration/Hydratable";
import { sortBatches, sortDocs } from "../../models/SweetListSorter";
import { AcceleratorDocMetadataStore } from "../../stores/impl/AcceleratorDocMetadataStore";
import { SELECT_ALL_BATCH_INSTANCES } from "../../stores/impl/AcceleratorDocStore";
import { BatchClassConfigStore } from "../../stores/impl/BatchClassConfigStore";
import { DocumentStatus } from "../../types/DocumentStatuses";
import { AcceleratorDocHydrator } from "../AcceleratorDocHydrator";
import { AcceleratorDocStatusCalculator } from "../AcceleratorDocStatusCalculator";

export { ConfigUiVisibleDocumentsService, ConfigUiVisibleDocumentsServiceImpl };

type DocStatusCount = { [x in DocumentStatus]: number };
/**
 * Store a reference to all batches.
 */
interface ConfigUiVisibleDocumentsService {
  /**
   * Returns the number of times each DocumentStatus appears within the visible AcceleratorDocs.
   * @returns {DocStatusCount}
   */
  getVisibleDocStatuses(): DocStatusCount;
  /* Set all Batches referenceble to consumer. */
  setBatches(batches: AcceleratorBatch[]): void;
  /* Add Batches referenceble to consumer. */
  addBatches(batches: AcceleratorBatch[]): void;
  /* Remove Batches. */
  removeBatches(batchUuids: string[]): void;
  /* Get all AccBatches referenceble to consumer. Always returned searched and sorted. */
  getBatches(): AcceleratorBatch[];
  getAllBatches(): AcceleratorBatch[];

  /* Get all referenceble AccBatches visible to consumer.  */
  getVisibleBatches(): AcceleratorBatch[];
  /* Get all cached, referenceble Hydratable<AcceleratorDocs> visible to consumer.  */
  getVisibleDocuments(): Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>[];
  /* Get all referenceble AcceleratorDocMetadata visible to consumer.  */
  getVisibleDocumentMetadata(): AcceleratorDocMetadata[];
  /* Get all AcceleratorDocMetadata referenceble to consumer.  */
  getAllDocumentMetadata(): AcceleratorDocMetadata[];

  /**
   * Changes currently selected AcceleratorDoc. If the AcceleratorDoc is not visible due to the current
   * batchFilter, the batchFilter will be updated.
   *
   * @param key AcceleratorDoc to change to.
   * @returns promise to use async to ensure Document data fully loaded before returning.
   */
  changeDocument(key: AcceleratorDocStoreKey): Promise<boolean>;
  /* Set currently visible AcceleratorBatch by UUID. Accepts 'ALL' as a filter. */
  setBatchFilter(batchUuid: string): void;
  /* Get current Batch Filter. */
  getBatchFilter(): string;

  /* Set uuids of AcceleratorBatches that have been searched */
  setSearchedBatches(batchUuids: string[] | null, filter?: string): void;
  /* Get currently searched AcceleratorBatches. */
  getSearchedBatches(): string[] | null;
  getLastSearchFilter(): string;

  /* Gets current Hydratable<AcceleratorDoc>. */
  getAcceleratorDoc():
    | Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>
    | undefined;
  /* Gets current AcceleratorDocMetadata. */
  getAcceleratorDocMetadata(): AcceleratorDocMetadata | undefined;
  /* Returns true if any AcceleratorDoc is visible. */
  isDocVisible(
    key?: AcceleratorDocStoreKey,
    docStatusFilter?: DocumentStatus,
  ): boolean;
  /* Determines if an AcceleratorDocMetadata contains a valid status. TODO: support AccelreatorDoc ?  */
  metadataHasStatus(
    acceleratorDocMetadata: AcceleratorDocMetadata,
    status?: DocumentStatus,
  ): boolean;
}

// TODO: test
class ConfigUiVisibleDocumentsServiceImpl implements ConfigUiVisibleDocumentsService {
  private acceleratorDocHydrator: AcceleratorDocHydrator;
  currentBatches: AcceleratorBatch[] = [];
  /**
   * Set when currentBatches is sorted
   */
  _sortedBatchUuids: string[] = [];
  _visibleDocumentMetadata?: AcceleratorDocMetadata[] = undefined;

  /* AcceleratorDocStoreKey provides current BatchInstance UUID, docId */
  /* Current Doc Page Index tracks which page is "active" */
  currentFilter: {
    accDocKey?: AcceleratorDocStoreKey;
    batchUuidFilter: string;
    searchedBatches: string[] | null;
  };

  lastSearchTerm: string;

  constructor(
    acceleratorDocHydrator: AcceleratorDocHydrator,
    selectedBatch: AcceleratorBatch | undefined,
    batches: AcceleratorBatch[],
  ) {
    this.acceleratorDocHydrator = acceleratorDocHydrator;
    this.setBatches(batches);
    this.currentFilter = {
      accDocKey: selectedBatch
        ? {
            batchInstanceUuid: selectedBatch!.uuid ?? "",
            docId: selectedBatch!.metadata[0].storeId.docId ?? "DOC0",
          }
        : undefined,
      batchUuidFilter: selectedBatch?.uuid ?? "",
      searchedBatches: null,
    };
    this.lastSearchTerm = "";
  }

  setSearchedBatches(batchUuids: string[] | null, filter?: string): void {
    this.currentFilter.searchedBatches = batchUuids;
    if (filter != undefined) {
      this.lastSearchTerm = filter;
    }
    this._wipeAcceleratorDocMetadataCache();
  }
  getSearchedBatches(): string[] | null {
    return this.currentFilter.searchedBatches;
  }
  getLastSearchFilter(): string {
    return this.lastSearchTerm;
  }

  private setFilterForNewBatches(
    selectedBatch: AcceleratorBatch | undefined,
    batches: AcceleratorBatch[],
  ) {
    const sb = selectedBatch ?? batches[0];
    if (!sb) {
      this.currentFilter = {
        batchUuidFilter: "",
        accDocKey: undefined,
        searchedBatches: null,
      };
    } else {
      this.currentFilter = {
        accDocKey: {
          batchInstanceUuid: sb.uuid ?? "",
          docId: sb.metadata[0].storeId.docId ?? "DOC0",
        },
        batchUuidFilter: sb.uuid ?? "",
        searchedBatches: null,
      };
    }
    this.lastSearchTerm = "";
  }

  isDocVisible(
    key?: AcceleratorDocStoreKey,
    docStatusFilter?: DocumentStatus,
  ): boolean {
    if (!key) return false;
    const docMetadata = this.getVisibleDocumentMetadata().find((x) =>
      compareAccDocKey(x.storeId, key),
    );
    if (docMetadata) {
      return this.metadataHasStatus(docMetadata, docStatusFilter);
    }
    return docMetadata != undefined;
  }
  getVisibleDocStatuses(): DocStatusCount {
    return AcceleratorDocStatusCalculator.sumDocStatuses(
      this.getVisibleDocumentMetadata(),
    );
  }
  addBatches(batches: AcceleratorBatch[]): void {
    this._visibleDocumentMetadata = undefined;

    this.currentBatches.push(...batches);
    this.sortBatches();
  }
  setBatchFilter(batchUuid: string): void {
    // console.trace(`setBatchFilter(${batchUuid})`)
    this._visibleDocumentMetadata = undefined;

    this.currentFilter.batchUuidFilter = batchUuid;
  }
  getBatchFilter(): string {
    return this.currentFilter.batchUuidFilter;
  }

  getAcceleratorDoc():
    | Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>
    | undefined {
    const batch = this.currentBatches.find(
      (x) => x.uuid === this.currentFilter.accDocKey?.batchInstanceUuid,
    );
    if (!batch) {
      console.warn("Cannot get AcceleratorDoc if AccBatch missing");
      // return HydratableFactory.getAcceleratorDoc(async () => undefined, this.currentFilter.accDocKey ??
      // { batchInstanceUuid: '', docId: '' });
      return undefined;
    }
    const batchClassConfig = BatchClassConfigStore.getWithoutLookup(
      batch.metadata[0].batchClassId,
    );
    if (!batchClassConfig) {
      console.warn("Cannnot load AcceleratorDoc when BCC is missing");
    }

    // TODO: test that deleted batches aren't cached
    return this.acceleratorDocHydrator.load(
      this.currentFilter.accDocKey!,
      batch.uuid,
      batchClassConfig!,
    );
  }

  getAcceleratorDocMetadata(): AcceleratorDocMetadata | undefined {
    // TODO: handle nulls
    if (this.currentFilter.accDocKey == undefined) return undefined;
    return AcceleratorDocMetadataStore.getWithoutLookup(
      this.currentFilter.accDocKey,
    );
  }

  removeBatches(batchUuids: string[]): void {
    this._visibleDocumentMetadata = undefined;
    const batchesToRemove = this.currentBatches.filter((x) =>
      batchUuids.includes(x.uuid),
    );
    batchesToRemove
      .flatMap((x) => x.metadata)
      .forEach((x) => this.acceleratorDocHydrator.delete(x.storeId));
    this.currentBatches = this.currentBatches.filter(
      (x) => !batchUuids.includes(x.uuid),
    );
    this.clearDocIfNotInBatches();
    // does not need sort on remove
  }

  private clearDocIfNotInBatches() {
    if (
      !this.currentBatches
        .map((x) => x.uuid)
        .includes(this.currentFilter.batchUuidFilter)
    ) {
      this.currentFilter.batchUuidFilter = this.currentBatches[0]?.uuid;
      this.currentFilter.accDocKey =
        this.currentBatches[0]?.metadata[0]?.storeId;
    }
    // does not need sort on remove
  }
  /**
   * Sort batches ordered by lastModified
   */
  sortBatches() {
    sortBatches(this.currentBatches);
    this._sortedBatchUuids = this.currentBatches.map((x) => x.uuid);
  }
  /**
   * Sort metadata by doc Id
   */
  sortMetadata() {
    if (this._visibleDocumentMetadata == undefined) return;

    sortDocs(this._visibleDocumentMetadata);

    this._visibleDocumentMetadata.sort(({ storeId: a }, { storeId: b }) => {
      return (
        this._sortedBatchUuids.indexOf(a.batchInstanceUuid) -
        this._sortedBatchUuids.indexOf(b.batchInstanceUuid)
      );
    });
  }

  // resets seaerch terms and such when BCC changes
  setBatches(batches: AcceleratorBatch[]): void {
    this._visibleDocumentMetadata = undefined;
    this.currentBatches = batches;
    this.sortBatches();
    // this.clearDocIfNotInBatches();
    this.setFilterForNewBatches(undefined, this.currentBatches);
  }
  getBatches(): AcceleratorBatch[] {
    if (this.currentFilter.searchedBatches) {
      return this.currentBatches.filter((x) =>
        this.currentFilter.searchedBatches?.includes(x.uuid),
      );
    }
    return this.currentBatches;
  }
  getAllBatches(): AcceleratorBatch[] {
    return this.currentBatches;
  }
  getVisibleBatches(): AcceleratorBatch[] {
    return (
      this.currentFilter.batchUuidFilter === SELECT_ALL_BATCH_INSTANCES
        ? this.getBatches()
        : [
            this.getBatches()!.find(
              (bi) => bi.uuid === this.currentFilter.batchUuidFilter,
            ),
          ].filter(Boolean)
    ) as AcceleratorBatch[];
  }

  /* TODO: optimize this method
   * avoid multiple batchClassConfigStore lookups, should only need to be done once. move bAtchClassIds on AccBatch
   * to facilitate.
   */
  getVisibleDocuments(): Hydratable<AcceleratorDoc, AcceleratorDocStoreKey>[] {
    return this.getVisibleBatches().flatMap((batch) => {
      //FIXME: batchCLassId should be on AccBatch
      //
      const batchClassConfig = BatchClassConfigStore.getWithoutLookup(
        batch.metadata[0].batchClassId,
      );
      if (!batchClassConfig) {
        console.warn("Cannnot load AcceleratorDoc when BCC is missing");
      }

      // only want to call this if doc not already loaded :/
      // TODO: test that deleted batches aren't cached
      return batch.metadata.map((doc) =>
        this.acceleratorDocHydrator.load(
          doc.storeId,
          batch.uuid,
          batchClassConfig!,
        ),
      );
    });
  }

  getVisibleDocumentMetadata(): AcceleratorDocMetadata[] {
    if (this._visibleDocumentMetadata == undefined) {
      this._visibleDocumentMetadata = this.getVisibleBatches()
        .flatMap((x) => x.metadata)
        .map((x) => AcceleratorDocMetadataStore.getWithoutLookup(x.storeId))
        .filter((x): x is AcceleratorDocMetadata => x !== undefined);
      this.sortMetadata();
    }
    return this._visibleDocumentMetadata;
  }

  getAllDocumentMetadata(): AcceleratorDocMetadata[] {
    return this.getAllBatches()
      .flatMap((x) => x.metadata)
      .map((x) => AcceleratorDocMetadataStore.getWithoutLookup(x.storeId))
      .filter((x): x is AcceleratorDocMetadata => x !== undefined);
  }

  setAllBatchesVisible() {
    this.currentFilter.batchUuidFilter = SELECT_ALL_BATCH_INSTANCES;
    this._visibleDocumentMetadata = undefined;
  }

  async changeDocument(key: AcceleratorDocStoreKey): Promise<boolean> {
    this.currentFilter.accDocKey = key;

    // determine if Batch UUID filter should be updated
    if (
      // if filter is undefined, always set filter
      this.currentFilter.batchUuidFilter == undefined ||
      // if filter is not ALL and the filter is not the Batch UUID of the current AcceleratorDoc
      (this.currentFilter.batchUuidFilter !== SELECT_ALL_BATCH_INSTANCES &&
        this.currentFilter.batchUuidFilter !== key.batchInstanceUuid)
    ) {
      this._visibleDocumentMetadata = undefined;
      if ((key.batchInstanceUuid ?? "").length !== 0) {
        this.currentFilter.batchUuidFilter =
          key.batchInstanceUuid ?? this.currentFilter.batchUuidFilter;
      } else {
        console.warn(
          "Cannot change visible Batch UUID filter when changing to AcceleratorDocStoreKey",
          key,
        );
      }
    }
    return false;
  }

  metadataHasStatus(
    acceleratorDocMetadata: AcceleratorDocMetadata,
    filter?: DocumentStatus,
  ): boolean {
    if (!filter) return true;
    return (
      acceleratorDocMetadata.statuses.find((status) => status === filter) !=
      undefined
    );
  }

  _wipeAcceleratorDocMetadataCache() {
    this._visibleDocumentMetadata = undefined;
  }
}
